import { useLazyGetSlotPageQuery, useLazyGetSlotsTitlesQuery } from "../../../api/controllers/slot"
import { getSlotsFields, getVisitorFields, parseFromVisitorFields } from "../../../utility/channels/slotsParser"
import { useAppSelector } from "../../../store/hooks"
import { selectCurrentProjectId } from "../../../store/projects/selectors"
import { GetSlotPageRequest } from "../../../models/slot"
import {
    PaginationSelectBase,
    PaginationSelectBaseProps
} from "../baseComponents/PaginationSelectBase/PaginationSelectBase"
import ErrorMessage from "../../ErrorMessage/ErrorMessage"
import PaginationMultiSelectBase from "../baseComponents/PaginationMultiSelectBase/PaginationMultiSelectBase"
import { PAGINATION_SELECT_PAGE_SIZE } from "../baseComponents/helpers/const"
import { useCallback } from "react"
import { FormPaginationSelect } from "../baseComponents/FormPaginationSelect/FormPaginationSelect"
import { GetOptionsType, PaginationSelectChangeType } from "../baseComponents/helpers/types"
import { PaginationRequestOrder } from "../../../models/pagination"

export interface SlotPaginationSelectProps
    extends Omit<PaginationSelectBaseProps, "getOptions" | "options" | "onChange"> {
    name?: string
    onChange?: PaginationSelectChangeType
    isMulti?: boolean
    filter?: Set<string>
    isWebchatSlots?: boolean
    useExtIdAsTittle?: boolean
}

type GetInitialLabelResult<T extends string | Array<string>> = T extends string ? string : Record<string, string>

export const SlotPaginationSelect = (props: SlotPaginationSelectProps) => {
    const {
        label,
        onChange,
        name,
        isMulti = false,
        dataTestId,
        filter,
        isWebchatSlots = false,
        useExtIdAsTittle = false,
        ...restProps
    } = props
    const [getSlots, getSlotsResult] = useLazyGetSlotPageQuery()
    const [getSlotsTitles, getTitlesResult] = useLazyGetSlotsTitlesQuery()
    const projectId = useAppSelector(selectCurrentProjectId) ?? ""
    const isLoading = getSlotsResult.isLoading || getSlotsResult.isFetching
    const isTitlesLoading = getTitlesResult.isLoading || getTitlesResult.isFetching

    const getSlotOptions: GetOptionsType = useCallback(
        async (text: string, page: number) => {
            const search = text.length > 0 ? text : undefined
            const getSlotParams: GetSlotPageRequest = {
                projectId: projectId,
                body: {
                    Search: search,
                    PageSize: PAGINATION_SELECT_PAGE_SIZE,
                    PageFrom: page,
                    Order: PaginationRequestOrder.Alphabet
                }
            }
            const result = await getSlots(getSlotParams)
            const slots = result?.data?.Items ?? []
            const totalPages = result?.data?.PageTotal ?? 0

            const options = getVisitorFields(slots, isWebchatSlots, useExtIdAsTittle)
            if (filter) {
                const filteredOptions = options.filter(option => !filter.has(option.value))
                return { options: filteredOptions, totalPages }
            }
            return { options, totalPages }
        },
        [getSlots, projectId, filter, isWebchatSlots, useExtIdAsTittle]
    )

    const getInitialLabel = useCallback(
        async <T extends string | Array<string>>(value: T): Promise<GetInitialLabelResult<T>> => {
            if (typeof value === "string") {
                if (useExtIdAsTittle) {
                    return String(value) as GetInitialLabelResult<T>
                }

                const parsedSlots = isWebchatSlots ? parseFromVisitorFields(value) : value
                const result = await getSlotsTitles({ projectId, slots: [parsedSlots] })
                const titles = result?.data ?? {}
                const title = titles[value] ? titles[value] : value
                return title as GetInitialLabelResult<T>
            }

            if (useExtIdAsTittle) {
                const titles = value.reduce<Record<string, string>>((acc, val) => {
                    acc[val] = val
                    return acc
                }, {})
                return titles as GetInitialLabelResult<T>
            }

            const parsedSlots = getSlotsFields(value, isWebchatSlots)
            const result = await getSlotsTitles({ projectId, slots: parsedSlots })
            const titles = result?.data ?? {}
            return titles as GetInitialLabelResult<T>
        },
        [getSlotsTitles, projectId, useExtIdAsTittle]
    )

    const renderSelect = (name?: string, isMulti?: boolean, onChange?: PaginationSelectChangeType) => {
        if (isMulti && name) {
            return (
                <PaginationMultiSelectBase
                    getOptions={getSlotOptions}
                    name={name}
                    label={label}
                    isLoading={isLoading}
                    id={dataTestId}
                    getInitialLabel={getInitialLabel}
                    isTitlesLoading={isTitlesLoading}
                    {...restProps}
                />
            )
        }
        if (name) {
            return (
                <FormPaginationSelect
                    name={name}
                    getOptions={getSlotOptions}
                    getInitialLabel={getInitialLabel}
                    label={label}
                    isClearable={true}
                    isLoading={isLoading}
                    dataTestId={dataTestId}
                    {...restProps}
                />
            )
        }
        if (onChange) {
            return (
                <PaginationSelectBase
                    isClearable={true}
                    label={label}
                    onChange={onChange}
                    getOptions={getSlotOptions}
                    isLoading={isLoading}
                    dataTestId={dataTestId}
                    {...restProps}
                />
            )
        }

        return <ErrorMessage text={"Invalid props"} />
    }

    return renderSelect(name, isMulti, onChange)
}
